import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import { formatCurrency } from "../../utils/Utils";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";
import { mockWorkOrders } from "../../data/mockWorkOrders";
import WorkOrderAside from "./workOrderAside";
import {
  Drawer,
  Button,
  Badge,
  Select,
  Input,
  DatePicker,
  Form,
  Row,
  Col,
  Space,
  Divider,
  Tag,
} from "antd";
import {
  FilterOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FilterState {
  workOrderNumber?: string;
  workOrderTitle?: string;
  status?: string[];
  siteId?: string[];
  organisation?: string[];
  contractType?: string[];
  createdDateRange?: [any, any] | null;
  releasedDateRange?: [any, any] | null;
  grossValueRange?: { min?: number; max?: number };
  workOrderOwner?: string;
}

const ViewAllWorkOrders: React.FC = () => {
  const navigate = useNavigate();
  const [sm, setSm] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [filterDrawerVisible, setFilterDrawerVisible] =
    useState<boolean>(false);
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [form] = Form.useForm();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [selectedKPIFilter, setSelectedKPIFilter] = useState<string>("status");

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "object" && value !== null) {
          if ("min" in value && value.min !== undefined) count++;
          if ("max" in value && value.max !== undefined) count++;
        } else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  const getStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      "Changes Requested": "processing",
      "In Execution": "warning",
      Approved: "success",
      Rejected: "error",
      Released: "success",
    };
    return statusColors[status] || "default";
  };

  // KPI Filter Categories
  const kpiFilterOptions = [
    { label: "By Status", value: "status" },
    { label: "By Value", value: "value" },
    { label: "By Time", value: "time" },
  ];

  const getWidgetsByFilter = (filterType: string) => {
    const totalOrders = mockWorkOrders.length;
    const totalValue = mockWorkOrders.reduce(
      (sum: any, wo: any) => sum + wo.woValue,
      0
    );
    const pendingCount = mockWorkOrders.filter(
      (wo: any) => wo.status === "Pending"
    ).length;
    const acceptedCount = mockWorkOrders.filter(
      (wo: any) => wo.status === "Accepted"
    ).length;
    const rejectedCount = mockWorkOrders.filter(
      (wo: any) => wo.status === "Rejected"
    ).length;
    const negotiationCount = mockWorkOrders.filter(
      (wo: any) => wo.status === "Under Negotiation"
    ).length;
    const finalizedCount = mockWorkOrders.filter(
      (wo: any) => wo.status === "Finalized"
    ).length;
    const slaBreachCount = mockWorkOrders.filter(
      (wo: any) => wo.slaAge > 2
    ).length;

    switch (filterType) {
      case "status":
        return [
          {
            title: "Total WOs",
            key: "total",
            color: "#495057",
            percentage: "100",
            value: `${totalOrders}`,
            icon: "📊",
          },
          {
            title: "Pending",
            key: "pending",
            color: "#FD7E14",
            percentage: `${((pendingCount / totalOrders) * 100).toFixed(1)}`,
            value: `${pendingCount}`,
            icon: "⏳",
          },
          {
            title: "Accepted",
            key: "accepted",
            color: "#28A745",
            percentage: `${((acceptedCount / totalOrders) * 100).toFixed(1)}`,
            value: `${acceptedCount}`,
            icon: "✅",
          },
          {
            title: "Under Negotiation",
            key: "negotiation",
            color: "#6610F2",
            percentage: `${((negotiationCount / totalOrders) * 100).toFixed(
              1
            )}`,
            value: `${negotiationCount}`,
            icon: "🔄",
          },
          {
            title: "Rejected",
            key: "rejected",
            color: "#DC3545",
            percentage: `${((rejectedCount / totalOrders) * 100).toFixed(1)}`,
            value: `${rejectedCount}`,
            icon: "❌",
          },
          {
            title: "Finalized",
            key: "finalized",
            color: "#198754",
            percentage: `${((finalizedCount / totalOrders) * 100).toFixed(1)}`,
            value: `${finalizedCount}`,
            icon: "🔒",
          },
          {
            title: "SLA Breach",
            key: "slaBreach",
            color: "#ff4d4f",
            percentage: `${((slaBreachCount / totalOrders) * 100).toFixed(1)}`,
            value: `${slaBreachCount}`,
            icon: "⚠️",
          },
        ];

      case "value":
        const paidAmount = totalValue * 0.6; // Mock calculation
        const outstanding = totalValue * 0.3;
        const retention = totalValue * 0.05;
        const onHold = totalValue * 0.05;

        return [
          {
            title: "Total WO Value",
            key: "totalValue",
            color: "#495057",
            percentage: "100",
            value: formatCurrency(totalValue, "INR"),
            icon: "💰",
          },
          {
            title: "Paid to Date",
            key: "paidToDate",
            color: "#198754",
            percentage: `${((paidAmount / totalValue) * 100).toFixed(1)}`,
            value: formatCurrency(paidAmount, "INR"),
            icon: "💸",
          },
          {
            title: "Outstanding",
            key: "outstanding",
            color: "#FD7E14",
            percentage: `${((outstanding / totalValue) * 100).toFixed(1)}`,
            value: formatCurrency(outstanding, "INR"),
            icon: "📋",
          },
          {
            title: "Retention",
            key: "retention",
            color: "#17A2B8",
            percentage: `${((retention / totalValue) * 100).toFixed(1)}`,
            value: formatCurrency(retention, "INR"),
            icon: "🔒",
          },
          {
            title: "On-Hold",
            key: "onHold",
            color: "#FFC107",
            percentage: `${((onHold / totalValue) * 100).toFixed(1)}`,
            value: formatCurrency(onHold, "INR"),
            icon: "⏸️",
          },
        ];

      case "time":
        return [
          {
            title: "Avg Response Time",
            key: "avgResponse",
            color: "#6610F2",
            percentage: "Target: 3 days",
            value: "2.5 days",
            icon: "⏱️",
          },
          {
            title: "Avg Processing Time",
            key: "avgProcessing",
            color: "#007BFF",
            percentage: "Target: 30 days",
            value: "25 days",
            icon: "🔄",
          },
        ];

      default:
        return [];
    }
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 250,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              onClick={() =>
                navigate(`/view-work-order-details/${record?.workOrderId}`)
              }
            >
              {text}
            </span>
            {/* <TooltipComponent
              iconClass="info-circle text-info"
              icon="info"
              id={`woStatus-${record?.workOrderId}`}
              direction="right"
              text="Click To View Work Order Details"
              containerClassName={"mt-1"}
            /> */}
          </>
        );
      },
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 220,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 27)}... ` : text}
            </span>
            {/* {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )} */}
          </>
        );
      },
    },
    {
      title: "Site / Project",
      key: "siteName",
      width: 200,
      render: (text: any, record: any) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName || "N/A"}</div>
        </div>
      ),
    },
    {
      title: "Organisation",
      dataIndex: "orgName",
      key: "orgName",
      width: 250,
    },
    // {
    //   title: "Contractor",
    //   dataIndex: "contractorName",
    //   key: "contractorName",
    //   width: 200,
    //   render: (text: string, record: any, index: any) => {
    //     return (
    //       <>
    //         <span id={`contractor-tooltip-${index}`}>
    //           {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
    //         </span>
    //         {text?.length > 25 && (
    //           <TooltipComponent
    //             iconClass="info-circle text-info"
    //             icon="info"
    //             id={`contractor-tooltip-${index}`}
    //             direction="top"
    //             text={text}
    //             containerClassName={""}
    //           />
    //         )}
    //       </>
    //     );
    //   },
    // },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status: string, record: any) => {
        const statusStyle = getStatusColor(status);
        const isSlaBreach = record.slaAge > 2 && status === "Pending";

        return <Tag color={getStatusColor(status)}>{status}</Tag>;
      },
    },
    {
      title: "Date Released",
      dataIndex: "dateReleased",
      key: "dateReleased",
      width: 150,
      render: (date: any) => <span>{FormatDate(date)}</span>,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 150,
      render: (text: any) => (
        <div style={{ textAlign: "right" }}>
          <span className="fw-bold">{formatCurrency(text, "INR")}</span>
        </div>
      ),
    },
    {
      title: "Last Action",
      key: "lastAction",
      width: 150,
      // render: (text: any, record: any) => {
      //   const lastAction =
      //     record.contractorActions[record.contractorActions.length - 1];
      //   return lastAction ? (
      //     <div>
      //       <div className="fw-bold">
      //         {lastAction.actionType.replace("_", " ").toUpperCase()}
      //       </div>
      //       <div className="text-muted small">
      //         {FormatDate(lastAction.timestamp)}
      //       </div>
      //     </div>
      //   ) : (
      //     <span className="text-muted">No action</span>
      //   );
      // },
    },
    {
      title: "SLA Age",
      dataIndex: "slaAgingForConfirmation",
      key: "slaAgingForConfirmation",
      width: 100,
      render: (days: any) => (
        <div style={{ textAlign: "center" }}>
          <span
            className={`badge ${
              days > 2 ? "bg-danger" : days > 1 ? "bg-warning":days == "NA" ? "" : "bg-success"
            }`}
          >
            {days !== "NA" ? `${days} days` : "--"}
          </span>
        </div>
      ),
    },
    {
      title: "Contract Owner",
      dataIndex: "workOrderOwnerName",
      key: "workOrderOwnerName",
      width: 150,
    },
  ];

  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // KPI Filter Change Handler
  const handleKPIFilterChange = (filterValue: string) => {
    setSelectedKPIFilter(filterValue);
  };

  // Filter Functions
  const handleFilterDrawerOpen = () => {
    setTempFilters({ ...filters });
    form.setFieldsValue(filters);
    setFilterDrawerVisible(true);
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    form.resetFields();
  };

  const handleApplyFilters = () => {
    form
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          workOrderNumber: values.workOrderNumber?.trim() || undefined,
          workOrderTitle: values.workOrderTitle?.trim() || undefined,
          status:
            values.status && values.status.length > 0
              ? values.status
              : undefined,
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          organisation:
            values.organisation && values.organisation.length > 0
              ? values.organisation
              : undefined,
          contractType:
            values.contractType && values.contractType.length > 0
              ? values.contractType
              : undefined,
          createdDateRange: values.createdDateRange || undefined,
          releasedDateRange: values.releasedDateRange || undefined,
          grossValueRange: {
            min: values.grossValueMin || undefined,
            max: values.grossValueMax || undefined,
          },
          workOrderOwner: values.workOrderOwner?.trim() || undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Remove empty grossValueRange
        if (
          processedFilters.grossValueRange &&
          !processedFilters.grossValueRange.min &&
          !processedFilters.grossValueRange.max
        ) {
          delete processedFilters.grossValueRange;
        }

        setFilters(processedFilters);
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    form.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      form.setFieldsValue(savedFilter);
    }
  };

  const statusOptions = [
    { label: "Pending", value: "Pending" },
    { label: "Accepted", value: "Accepted" },
    { label: "Rejected", value: "Rejected" },
    { label: "Under Negotiation", value: "Under Negotiation" },
    { label: "Finalized", value: "Finalized" },
  ];

  const uniqueOrgs = Array.from(
    new Set(mockWorkOrders.map((wo: any) => wo.organisation))
  ).map((org) => ({
    label: org,
    value: org,
  }));

  const contractTypeOptions = [
    { label: "Fixed Price", value: "Fixed Price" },
    { label: "Time & Material", value: "Time & Material" },
    { label: "Cost Plus", value: "Cost Plus" },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="view-all-work-orders"
        title="View All Work Orders"
        columns={columns}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <WorkOrderAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 2200,
          dateRange: false,
          excelExport: true,
          dataFilterTootip: "Filtering based on Work Order created date",
        }}
        // widget={{
        //   isActive: true,
        //   compactGrid: true,
        //   kpiFilter: {
        //     isActive: true,
        //     options: kpiFilterOptions,
        //     selectedFilter: selectedKPIFilter,
        //     onFilterChange: handleKPIFilterChange,
        //   },
        //   list: getWidgetsByFilter(selectedKPIFilter),
        // }}
      />

      <Drawer
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span>Advanced Filters</span>
            <Space>
              <Badge count={getActiveFilterCount()} size="small">
                <Button size="small" type="text" icon={<FilterOutlined />} />
              </Badge>
            </Space>
          </div>
        }
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="workOrderNumber" label="Work Order Number">
                <Input
                  placeholder="Enter WO Number"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderTitle" label="Work Order Title">
                <Input
                  placeholder="Enter WO Title"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="status" label="Status">
                <Select
                  mode="multiple"
                  placeholder="Select Status"
                  options={statusOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="siteId" label="Site">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="organisation" label="Organisation">
                <Select
                  mode="multiple"
                  placeholder="Select Organisations"
                  options={uniqueOrgs}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="contractType" label="Contract Type">
                <Select
                  mode="multiple"
                  placeholder="Select Contract Types"
                  options={contractTypeOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderOwner" label="Work Order Owner">
                <Input
                  placeholder="Enter Owner Name"
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Date Ranges
              </Divider>
            </Col>

            <Col span={24}>
              <Form.Item name="createdDateRange" label="Created Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="releasedDateRange" label="Released Date Range">
                <RangePicker
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  placeholder={["Start Date", "End Date"]}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Divider orientation="left" orientationMargin="0">
                Value Range
              </Divider>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMin" label="Min Value">
                <Input
                  type="number"
                  placeholder="Minimum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="grossValueMax" label="Max Value">
                <Input
                  type="number"
                  placeholder="Maximum"
                  addonBefore="₹"
                  min={0}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default ViewAllWorkOrders;
